interface FocusTrapOptions {
	clickOutsideDeactivates?: boolean;
	/**
	 * isElementAllowedOutsideOfTrap will stop FocusTrap from forcing the focused element
	 * to exist in the given container. This function exists to allow interaction with
	 * datepickers and autocomplete fields that exist just underneath the <body> tag.
	 */
	isElementAllowedOutsideOfTrap?: (element: HTMLElement) => boolean;
}

const errNoTabbableElements = new Error(
	'No tabbable elements found inside given container.',
);

/**
 * FocusTrap library based off of:
 * https://github.com/davidtheclark/focus-trap
 *
 * How to use:
 * this.focusTrap = new FocusTrap(this.refRootElem.current);
 * this.focusTrap.enable();
 *
 * We needed to roll our own library as we need support for focusing elements
 * that exist outside of the focus trap.
 * ie. Using Flatpickr which renders underneath the <body> tag.
 *
 * Using the aforementioned library was also incompatible with Jest tests because
 * "HTMLElement.offsetParent" is not supported by jsdom. However this was fixed by
 * patching our Jest config.
 * (Resolved with: https://github.com/jsdom/jsdom/issues/1261#issuecomment-362928131)
 */
export class FocusTrap {
	private readonly container: HTMLElement;

	private readonly options: FocusTrapOptions;

	private enabled: boolean = false;

	private lastFocusedNode?: HTMLElement;

	constructor(container: HTMLElement, options?: FocusTrapOptions) {
		this.container = container;
		this.options = options ? options : {};
	}

	public enable = (): void => {
		if (this.enabled) {
			return;
		}
		if (!this.canEnable()) {
			throw errNoTabbableElements;
		}
		if (document.activeElement instanceof HTMLElement) {
			if (this.container.contains(document.activeElement)) {
				this.lastFocusedNode = document.activeElement;
			} else {
				// NOTE: Jake: 2019-07-15
				// if currently outside of the FocusTrap, force us to be inside it.
				const tabbableNodes = FocusTrap.getTabbableNodes(
					this.container,
				);
				if (tabbableNodes.length === 0) {
					throw errNoTabbableElements;
				}
				let firstEl: HTMLElement;
				for (const el of tabbableNodes) {
					if (el.getAttribute('disabled') || !el.offsetParent) {
						continue;
					}
					firstEl = el;
					break;
				}
				if (firstEl) {
					this.tryFocus(firstEl);
				}
			}
		}
		document.addEventListener('keydown', this.onKeyDown, {
			capture: true,
			passive: false,
		});
		document.addEventListener('mousedown', this.onMouseDownOrTouchStart, {
			capture: true,
			passive: false,
		});
		document.addEventListener('touchstart', this.onMouseDownOrTouchStart, {
			capture: true,
			passive: false,
		});
		document.addEventListener('click', this.onClick, {
			capture: true,
			passive: false,
		});
		document.addEventListener('focusin', this.onFocusIn, true);
		this.enabled = true;
	};

	/**
	 * canEnable will return whether the focus trap can be enabled with an error being thrown or not.
	 */
	public canEnable = (): boolean => {
		const tabbableNodes = FocusTrap.getTabbableNodes(this.container);
		return tabbableNodes.length > 0;
	};

	public disable = (): void => {
		if (!this.enabled) {
			return;
		}
		document.removeEventListener('keydown', this.onKeyDown, true);
		document.removeEventListener(
			'mousedown',
			this.onMouseDownOrTouchStart,
			true,
		);
		document.removeEventListener(
			'touchstart',
			this.onMouseDownOrTouchStart,
			true,
		);
		document.removeEventListener('click', this.onClick, true);
		document.removeEventListener('focusin', this.onFocusIn, true);
		this.enabled = false;
	};

	/**
	 * onMouseDownOrTouchStart is where we block events as "mousedown" and "touchstart" fire
	 * before "focus" whereas "click" does not.
	 */
	private onMouseDownOrTouchStart = (e: TouchEvent | MouseEvent) => {
		if (
			e.target instanceof HTMLElement &&
			this.container.contains(e.target)
		) {
			return;
		}
		if (this.options.clickOutsideDeactivates) {
			this.disable();
			return;
		}
		e.preventDefault();
	};

	private onClick = (e: TouchEvent | MouseEvent) => {
		const target = e.target as HTMLElement;
		if (target instanceof HTMLElement && this.container.contains(target)) {
			return;
		}
		if (
			typeof this.options.isElementAllowedOutsideOfTrap === 'function' &&
			this.options.isElementAllowedOutsideOfTrap(target) === true
		) {
			return;
		}

		e.preventDefault();
		e.stopImmediatePropagation();
	};

	private onFocusIn = (e: FocusEvent): void => {
		const target = e.target as HTMLElement;
		if (this.container.contains(target)) {
			// NOTE: Jake: 2019-07-15
			// This allows us to snap back to the last element focused with tabbing
			// if *something* tries to focus outside of the container.
			// ie. Key event code focuses user to datepicker just under <body> tag
			this.lastFocusedNode = target;
			return;
		}
		if (
			(typeof this.options.isElementAllowedOutsideOfTrap === 'function' &&
				this.options.isElementAllowedOutsideOfTrap(target) === true) ||
			// In Firefox when you Tab out of an iframe the Document is briefly focused.
			// **untested note From focus-trap library mentioned above**
			target instanceof Document
		) {
			return;
		}
		e.stopImmediatePropagation();
		this.tryFocus(this.lastFocusedNode);
	};

	private onKeyDown = (e: KeyboardEvent): void => {
		// NOTE: Jake: 2019-07-16
		// Not using our constant 'Key' import here so that this code is dependency-less
		// and can be easily pulled out and put into other projects.
		if (e.key === 'Tab' || e.keyCode === 9) {
			// NOTE: Jake: 2019-07-15
			// Not caching / getting tabbable nodes each time "Tab" is hit so that if the DOM
			// has had elements inserted/removed, this will *just work*.
			const tabbableNodes = FocusTrap.getTabbableNodes(this.container);

			if (tabbableNodes.length > 0) {
				const firstTabbableNode = tabbableNodes[0];
				const lastTabbableNode =
					tabbableNodes[tabbableNodes.length - 1];

				if (e.shiftKey && e.target === firstTabbableNode) {
					e.preventDefault();
					this.tryFocus(lastTabbableNode);
					return;
				}
				if (!e.shiftKey && e.target === lastTabbableNode) {
					e.preventDefault();
					this.tryFocus(firstTabbableNode);
					return;
				}
			}
		}
	};

	private tryFocus = (el: HTMLElement | undefined): void => {
		if (!el || !el.focus) {
			return;
		}
		this.lastFocusedNode = el;
		if (el === document.activeElement) {
			// Don't call "focus" if already focused.
			return;
		}
		el.focus();
	};

	/**
	 * getTabbableNodes will get a list elements that can currently be tabbed to.
	 * This will exclude elements that are disabled, input hidden (type="hidden") or visually hidden ("display: none" or "visibility: hidden)
	 */
	public static getTabbableNodes(container: HTMLElement): HTMLElement[] {
		// Based on: github.com/davidtheclark/tabbable
		const tabbableCandidates = container.querySelectorAll<
			HTMLElement & { disabled?: boolean }
		>(
			[
				'input',
				'select',
				'textarea',
				'a[href]',
				'button',
				'[tabindex]',
				'audio[controls]',
				'video[controls]',
				'[contenteditable]:not([contenteditable="false"])',
			].join(','),
		);
		const result: HTMLElement[] = [];
		for (const node of tabbableCandidates) {
			// NOTE: Jake: 2019-08-28
			// node.offsetParent === null is the cheapest way to see if "display: none".
			if (
				node.offsetParent === null ||
				getComputedStyle(node, null).visibility === 'hidden' ||
				node.hidden ||
				node.disabled
			) {
				// Ignore detached / hidden elements
				continue;
			}
			result.push(node);
		}
		return result;
	}
}
